import type { Patch } from '../immer-types';
import type { QueryArgFrom, ResultTypeFrom, Recipe, PatchCollection } from '../rtk-query-types';

import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';

import type { AnyQueryDefinition } from './types';

/**
 * Набор методов для работы с кешом эндпоинта
 */
export type EndpointUtils<Definition extends AnyQueryDefinition> = {
    /**
     * Обновляет данные в кеше с помощью immer-рецепта.
     * Если данные отсутствуют в кеше, обновление не происходит.
     *
     * @param queryArgs - Параметры запроса для идентификации записи в кеше
     * @param recipe - Immer-рецепт для изменения данных
     * @returns Коллекция патчей с прямыми и обратными изменениями, а также методом undo для отката
     */
    updateQueryData: (
        queryArgs: QueryArgFrom<Definition>,
        recipe: Recipe<ResultTypeFrom<Definition>>
    ) => PatchCollection;
    /**
     * Обновляет данные в кеше или создает новую запись, если её нет.
     * В отличие от updateQueryData, этот метод работает даже если данных в кеше еще нет.
     *
     * @param queryArgs - Параметры запроса для идентификации записи в кеше
     * @param data - Данные для записи в кеш
     * @returns Promise, который разрешается после обновления кеша
     */
    upsertQueryData: (queryArgs: QueryArgFrom<Definition>, data: ResultTypeFrom<Definition>) => Promise<unknown>;
    /**
     * Применяет набор immer-патчей к данным в кеше.
     * Полезно для применения обратных патчей, полученных из updateQueryData.
     *
     * @param queryArgs - Параметры запроса для идентификации записи в кеше
     * @param patches - Массив immer-патчей для применения
     */
    patchQueryData: (queryArgs: QueryArgFrom<Definition>, patches: readonly Patch[]) => void;
};

// Параметры для `buildEndpointUtils()`.
type BuildEndpointUtilsOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
};

export function buildEndpointUtils({
    api,
    endpointName,
    getStore,
}: BuildEndpointUtilsOptions): EndpointUtils<AnyQueryDefinition> {
    return {
        updateQueryData: (queryArgs, recipe) =>
            getStore().dispatch(api.util.updateQueryData(endpointName, queryArgs, recipe)),
        upsertQueryData: (queryArgs, data) =>
            getStore().dispatch(api.util.upsertQueryData(endpointName, queryArgs, data)),
        patchQueryData: (queryArgs, patches) =>
            getStore().dispatch(api.util.patchQueryData(endpointName, queryArgs, patches)),
    };
}
